import React, { useState } from "react";
import { Layout, Modal, Tooltip, Button, Typography, Table } from "antd";
import { listEmployeeScheduling } from "../../../api/server";
import { CheckCircleOutlined, CloseOutlined } from "@ant-design/icons";
import { useIntl } from "react-intl";
import Date from "../../../components/tables/Date";
import Patient from "../../../components/tables/Patient";
import { getFullName } from "../../../components/util/helpers";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import moment from "moment";
import {
  SchedulingModalProps,
  SchedulingItem,
  QueryParams,
  EmployeeSchedulingResponse,
} from "../types";
import { Order } from "../../../types/order";

const { Text } = Typography;

/**
 * SchedulingModal Component
 *
 * Displays a modal with detailed scheduling information for an employee related to a specific order.
 * Shows scheduling items within a selected month, along with approval status and hour calculations.
 *
 * Features:
 * - Displays patient and client information
 * - Shows all scheduled appointments for the employee-patient-order combination
 * - Indicates both general approval and salary approval status
 * - Calculates and displays totals for scheduled, approved, and salary approved hours
 * - Handles both current and retroactive (past) scheduling data
 *
 * @param {SchedulingModalProps} props - Component properties
 * @param {number | null} props.orderId - ID of the order to show scheduling for
 * @param {Function} props.onHide - Callback function to close the modal
 * @param {Object} props.employee - Employee data including ID and name
 * @param {Moment} props.date - Current date context for the modal
 * @param {Moment | null} props.retroMonth - Optional retroactive month to show data from
 *
 * @example
 * ```tsx
 * <SchedulingModal
 *   orderId={123}
 *   onHide={() => setModalVisible(false)}
 *   employee={selectedEmployee}
 *   date={currentDate}
 *   retroMonth={retroactiveMonth}
 * />
 * ```
 */
const SchedulingModal: React.FC<SchedulingModalProps> = ({
  orderId,
  onHide,
  employee,
  date,
  retroMonth,
}) => {
  const intl = useIntl();
  const { branchId } = useParams<{ branchId: string }>();
  const [order, setOrder] = useState<Order | null>(null);

  // Redux selectors
  const activeYear = useSelector((state: any) => state.salary.activeYear);
  const activeMonth = useSelector((state: any) => state.salary.activeMonth);
  const scopePrefix = useSelector((state: any) => state.user.scopePrefix);

  // Determine active date
  const activeDate = retroMonth || date || moment();
  const queryParams: QueryParams = {
    filter: {
      date: {
        $gt: activeDate.startOf("month").format(),
        $lt: activeDate.endOf("month").format(),
      },
    },
  };

  /**
   * Fetches scheduling data for the selected employee and order
   * Filters results to include only items related to the specific order
   */
  const { isLoading, data } = useQuery<SchedulingItem[]>(
    [queryParams, scopePrefix, employee?.id, orderId],
    async () => {
      if (!employee || !orderId) return [];

      const params = {
        ...queryParams,
        scopePrefix,
        employeeId: employee.id,
        branchId,
      };

      const result = (await listEmployeeScheduling(
        params
      )) as EmployeeSchedulingResponse;
      const filtered = result.data.items.filter(
        (item) => item.order_id === orderId
      );

      const selectedOrder = result.data.effective_orders.find(
        (item) => item.id === orderId
      );

      if (selectedOrder) {
        setOrder(selectedOrder);
      }

      return filtered;
    },
    {
      keepPreviousData: true,
      staleTime: 0,
      enabled: !!employee?.id && !!orderId,
    }
  );

  /**
   * Calculates total hours from scheduling items based on an optional condition
   *
   * @param {SchedulingItem[] | undefined} items - List of scheduling items
   * @param {Function} condition - Optional filter condition to apply before sum
   * @returns {number} - Total hours calculated from minutes and rounded to 2 decimal places
   */
  const calculateHours = (
    items: SchedulingItem[] | undefined,
    condition: (item: SchedulingItem) => boolean = () => true
  ): number => {
    if (!items) return 0;

    return (
      Math.round(
        (items.reduce(
          (sum, item) => sum + (condition(item) ? item.duration_minutes : 0),
          0
        ) /
          60) *
          100
      ) / 100
    );
  };

  // Calculate summary statistics
  const totalHours = calculateHours(data);
  const approvedHours = calculateHours(data, (item) => item.approved);
  const salaryApprovedHours = calculateHours(
    data,
    (item) => item.salary_approved
  );

  /**
   * Table column definitions for scheduling data
   * Includes patient info, client name, dates, times and approval statuses
   */
  const columns = [
    {
      title: intl.formatMessage({ id: "app.scheduling.patientName" }),
      dataIndex: "patient",
      render: () =>
        order?.patient && (
          <Patient
            record={{
              id: order.patient.id,
              branch: order.patient.branch,
              first_name: order.patient.first_name,
              surename: order.patient.surename,
            }}
          />
        ),
      width: "220px",
    },
    {
      title: intl.formatMessage({ id: "app.scheduling.table.client" }),
      ellipsis: true,
      dataIndex: "order",
      render: () => order?.client?.name && <Text>{order.client.name}</Text>,
      width: "140px",
    },
    {
      title: intl.formatMessage({ id: "app.scheduling.date" }),
      dataIndex: "date",
      render: (text: string) => <Date text={text} format="L" />,
      width: "100px",
    },
    {
      title: intl.formatMessage({ id: "app.scheduling.dayInWeek" }),
      dataIndex: "date",
      render: (text: string) => (
        <Text>{moment.weekdays(moment(text).weekday())}</Text>
      ),
      width: "70px",
    },
    {
      title: intl.formatMessage({ id: "app.scheduling.start" }),
      dataIndex: "date",
      render: (text: string) => <Date text={text} format="HH:mm" />,
      width: "70px",
    },
    {
      title: intl.formatMessage({ id: "app.scheduling.end" }),
      dataIndex: "date",
      render: (text: string, record: SchedulingItem) => (
        <Date
          text={moment(text).add(record.duration_minutes, "minutes")}
          format="HH:mm"
        />
      ),
      width: "70px",
    },
    {
      title: intl.formatMessage({ id: "app.scheduling.approved" }),
      dataIndex: "approved",
      render: (text: boolean) =>
        text && <CheckCircleOutlined style={{ color: "#56C41A" }} />,
      width: "80px",
    },
    {
      title: intl.formatMessage({ id: "app.scheduling.salary_approved" }),
      dataIndex: "salary_approved",
      render: (text: boolean) =>
        text && <CheckCircleOutlined style={{ color: "#56C41A" }} />,
      width: "80px",
    },
  ];

  // Format the display date based on retroactive or current month
  const schedulingDate = retroMonth
    ? `${retroMonth.format("MM")}/${retroMonth.format("YYYY")}`
    : `${activeMonth}/${activeYear}`;

  return (
    <Modal
      width="80vw"
      style={{
        height: "80vh",
        maxWidth: "80vw",
        position: "absolute",
        top: "10vh",
        left: "10vw",
      }}
      closable={false}
      open={!!orderId}
      footer={null}
      destroyOnClose
    >
      <Layout style={{ background: "transparent", paddingTop: "20px" }}>
        <Tooltip title="close">
          <Button
            onClick={onHide}
            size="large"
            style={{ position: "absolute", right: 10, top: 10 }}
            shape="circle"
            icon={<CloseOutlined />}
          />
        </Tooltip>

        <div>
          {/* Summary Section - Displays approval statistics */}
          {data && (
            <div
              style={{
                padding: "10px 0",
                display: "flex",
                width: "100%",
                alignItems: "center",
                justifyContent: "center",
                marginTop: "-30px",
              }}
            >
              <Text style={{ fontSize: "1.2em" }}>
                {`${intl.formatMessage({ id: "app.scheduling.approved" })}: `}
                <span
                  style={{
                    fontWeight: "bold",
                    color: totalHours === approvedHours ? "#56C41A" : "inherit",
                  }}
                >
                  {`${approvedHours}/${totalHours}`}
                </span>
              </Text>
              <div style={{ width: "30px" }} />
              <Text style={{ fontSize: "1.2em" }}>
                {`${intl.formatMessage({
                  id: "app.scheduling.salary_approved",
                })}: `}
                <span
                  style={{
                    fontWeight: "bold",
                    color:
                      totalHours === salaryApprovedHours
                        ? "#56C41A"
                        : "inherit",
                  }}
                >
                  {`${salaryApprovedHours}/${totalHours}`}
                </span>
              </Text>
            </div>
          )}

          {/* Header Info - Displays context information */}
          {employee && order?.patient && (
            <div style={{ padding: "10px 0" }}>
              <Text>
                {intl.formatMessage({
                  id: "app.scheduling.employeeScheduling",
                })}
                <span style={{ fontWeight: "bold" }}>
                  {` ${getFullName(employee.surename, employee.first_name)} `}
                </span>
                {intl.formatMessage({ id: "app.scheduling.ofPatient" })}
                <span style={{ fontWeight: "bold" }}>
                  {` ${getFullName(
                    order.patient.surename,
                    order.patient.first_name
                  )} `}
                </span>
                {intl.formatMessage({ id: "app.scheduling.forClient" })}
                <span style={{ fontWeight: "bold" }}>
                  {` ${order?.client?.name} `}
                </span>
                {intl.formatMessage({ id: "app.scheduling.inMonth" })}
                <span style={{ fontWeight: "bold" }}>
                  {` ${schedulingDate}`}
                </span>
              </Text>
            </div>
          )}

          {/* Schedule Table - Displays the scheduling data */}
          <Table
            bordered
            dataSource={data || []}
            rowKey={(record) => record.id}
            columns={columns}
            pagination={false}
            scroll={{ y: window.innerHeight * 0.8 - 160 }}
            loading={isLoading}
          />
        </div>
      </Layout>
    </Modal>
  );
};

export default SchedulingModal;
